import React, { useState, useEffect } from 'react';
import { Video, Users, Globe, Zap, Clock, CheckCircle, Star, ArrowRight, Play } from 'lucide-react';

interface DemoUsage {
  allowed: boolean;
  usage: {
    total_minutes: number;
    session_count: number;
  };
  limits: {
    minutes: number;
    sessions: number;
  };
  remaining: {
    minutes: number;
    sessions: number;
  };
  vpnDetected: boolean;
  limitReached: boolean;
  signupUrl?: string;
}

export const DemoLanding: React.FC = () => {
  const [hostName, setHostName] = useState('');
  const [guestName, setGuestName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [demoUsage, setDemoUsage] = useState<DemoUsage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUsageStatus();
  }, []);

  const loadUsageStatus = async () => {
    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/status/check');
      if (response.ok) {
        const data = await response.json();
        setDemoUsage(data);
      }
    } catch (error) {
      console.error('Error loading usage status:', error);
    }
  };

  const handleStartDemo = async () => {
    if (!hostName.trim()) {
      setError('Please enter your name');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/public/demo/create-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hostName: hostName.trim(),
          guestName: guestName.trim() || 'Guest'
        })
      });

      const data = await response.json();

      if (data.error === 'demo_limit_reached') {
        window.location.href = data.signupUrl || 'https://app.customerconnect.live/signup';
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create demo session');
      }

      // Redirect to host interface
      window.location.href = data.urls.host;

    } catch (error) {
      console.error('Error creating demo:', error);
      setError(error instanceof Error ? error.message : 'Failed to create demo session');
    } finally {
      setIsCreating(false);
    }
  };

  // If user has hit limits, show conversion page
  if (demoUsage?.limitReached) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <Zap className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            You've tried our platform!
          </h1>
          <p className="text-gray-600 mb-6">
            Ready for unlimited video sessions, advanced features, and priority support?
          </p>
          <a
            href={demoUsage.signupUrl || 'https://app.customerconnect.live/signup'}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <ArrowRight className="w-5 h-5 mr-2" />
            Get Full Access
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-blue-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Video className="w-8 h-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">CustomerConnect</span>
          </div>
          <div className="text-sm text-gray-600">
            {demoUsage && (
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                {demoUsage.remaining.minutes} min • {demoUsage.remaining.sessions} sessions left
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Try Video Chat <span className="text-blue-600">Instantly</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Experience professional video conferencing in seconds. No signup required,
            no downloads needed - just enter your name and start connecting!
          </p>
        </div>

        {/* Social Proof */}
        <div className="flex items-center justify-center mb-8">
          <div className="bg-white/60 backdrop-blur-sm rounded-full px-6 py-3 flex items-center space-x-4">
            <div className="flex items-center text-sm text-gray-700">
              <Users className="w-4 h-4 mr-2" />
              <span className="font-semibold">1,247</span>
              <span className="ml-1">people tried today</span>
            </div>
            <div className="w-1 h-4 bg-gray-300"></div>
            <div className="flex items-center text-sm text-gray-700">
              <Star className="w-4 h-4 text-yellow-500 mr-1" />
              <span className="font-semibold">4.9</span>
              <span className="ml-1">rating</span>
            </div>
          </div>
        </div>

        {/* Demo Creation Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <div className="flex items-center mb-6">
              <Play className="w-8 h-8 text-blue-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Start Your Demo</h2>
            </div>
            
            {/* Usage Warning */}
            {demoUsage && (demoUsage.remaining.minutes <= 3 || demoUsage.remaining.sessions <= 3) && (
              <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center">
                <Clock className="w-5 h-5 text-orange-600 mr-2" />
                <span className="text-sm font-medium text-orange-800">
                  {demoUsage.remaining.minutes} minutes remaining in your free trial
                </span>
                <a
                  href="https://app.customerconnect.live/signup"
                  className="ml-auto text-orange-600 hover:text-orange-700 text-sm font-medium"
                >
                  Sign up for unlimited sessions and advanced features
                </a>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name (Host)
                </label>
                <input
                  type="text"
                  id="hostName"
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your name"
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Guest Name (Optional)
                </label>
                <input
                  type="text"
                  id="guestName"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Guest name (optional)"
                  maxLength={50}
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <button
                onClick={handleStartDemo}
                disabled={isCreating || !hostName.trim()}
                className="w-full bg-blue-600 text-white py-4 px-8 rounded-lg font-bold text-lg hover:bg-blue-700 disabled:opacity-75 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                    Creating Demo...
                  </>
                ) : (
                  <>
                    <Play className="w-6 h-6 mr-3" />
                    Start Video Demo
                  </>
                )}
              </button>

              <p className="text-xs text-center text-gray-500 mt-4">
                Your demo room will be created instantly. Share the guest link with anyone you want to connect with.
              </p>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Instant Setup</h3>
              <p className="text-sm text-gray-600">
                No registration required. Start your video demo in under 30 seconds.
              </p>
            </div>

            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                <Video className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">HD Quality</h3>
              <p className="text-sm text-gray-600">
                Crystal clear video and audio with enterprise-grade infrastructure.
              </p>
            </div>

            <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4">
                <Globe className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Works Everywhere</h3>
              <p className="text-sm text-gray-600">
                Compatible with all devices and browsers. No downloads needed.
              </p>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Trusted by businesses worldwide</h3>
            <div className="grid grid-cols-3 gap-6 text-center text-sm text-gray-600">
              <div className="flex items-center justify-center">
                <Users className="w-4 h-4 text-blue-500 mr-2" />
                <span className="font-semibold">500+</span>
                <span className="ml-1">companies</span>
              </div>
              <div className="flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                <span className="font-semibold">Enterprise</span>
                <span className="ml-1">grade security</span>
              </div>
              <div className="flex items-center justify-center">
                <Zap className="w-4 h-4 text-yellow-500 mr-2" />
                <span className="font-semibold">99.9%</span>
                <span className="ml-1">uptime</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-white/80 backdrop-blur-sm border-t border-blue-100">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <p className="text-sm text-gray-600 mb-4">
            Want unlimited sessions? {' '}
            <a 
              href="https://app.customerconnect.live/signup" 
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign up for free
            </a>
          </p>
          <div className="flex justify-center gap-6 text-sm">
            <a 
              href="/public/demo/monitor" 
              className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
            >
              📊 API Monitor
            </a>
            <a 
              href="https://github.com/your-repo" 
              className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
            >
              📚 API Docs
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};